import BaseWebUniversal, { BaseWebUniversalProps } from '../BaseWebUniversal'
import { columnsheadId, showText } from '../columnshead';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DatePicker, Select, Tooltip } from 'antd';
import BaseLoggerClear from './BaseLoggerClear';
import { useEffect, useRef, useState } from 'react';
import getList from '../../../universal/getList';
import { universalBasicType, universalBasicTypeFactory } from '../../../../utilsTs/declareTypes';

/** 日志属性 */
export interface BaseLoggerProps<
    F extends universalBasicTypeFactory<any> = universalBasicTypeFactory<Record<string, universalBasicType<string>>>
> {
    /** 日志服务地址 */
    loggerServiceUrl?: string;
    /** 日志类型对象 */
    LoggerTypes?: F;
    /** 日志类型id，默认: 'SYSTEM' */
    loggerTypeId?: F extends universalBasicTypeFactory<infer T> ? T[keyof T]['id'] : never;
    /** 可查询 */
    isQuery?: boolean;
    /** 可日志类型选择 */
    isLoggerTypeSelect?: boolean;
    /** 可日志日期选择 */
    isLoggerDateSelect?: boolean;
    /** 显示日志清除 */
    isLoggerClear?: boolean;
    /** 查询类型 */
    queryType?: string;
    /** 查询类型扩展id */
    queryTypeExtendId?: string;
    /** 查询id */
    queryId?: string;
};

/**
 * 日志
 * @param props 选项
 * @returns 
 */
const Logger = (props: BaseLoggerProps) => {
    const ref = useRef<any>(undefined);
    let { loggerServiceUrl, isQuery = true, queryType, queryTypeExtendId, queryId, LoggerTypes,
        loggerTypeId = 'SYSTEM', isLoggerClear = true,
        isLoggerTypeSelect = true, isLoggerDateSelect = true } = props;
    const [dateTime, setDateTime] = useState<any>(undefined);
    const [loggerTypeSelectDisabled, setLoggerTypeSelectDisabled] = useState(!isLoggerTypeSelect);
    const [loggerDateSelectDisabled, setLoggerDateSelectDisabled] = useState(!isLoggerDateSelect);
    /** apiId */
    const apiId = 'logger';
    /** 日志id */
    const [loggerTypeIdSelect, setLoggerTypeIdSelect] = useState<string>(loggerTypeId);
    const [loggerTypeData, setLoggerTypeData] = useState<any>();
    const [modalVisibleBaseLoggerClear, setModalVisibleBaseLoggerClear] = useState(false);

    useEffect(() => {
        setLoggerTypeData(LoggerTypes?.getAll());
        ref.current.fetchData({
            sendPageData: getSendPageData({ selectLoggerTypeId: loggerTypeIdSelect })
        })
    }, []);

    //表头
    const columnsHead = [
        {
            title: '时间',
            dataIndex: 'createdAt',
            render: (val: any) =>
                <span>
                    {dayjs(val).format('YYYY-MM-DD HH:mm:ss')}
                </span>,
            sorter: true,
        },
        {
            title: '级别',
            dataIndex: 'logsLevel',
            sorter: true,
            render: (val: any) => {
                let showText;
                switch (val?.id) {
                    case 0:
                        showText = (<span style={{ color: 'red' }}>{val?.name}</span>);
                        break;
                    case 1:
                        showText = (<span style={{ color: 'yellow' }}>{val?.name}</span>);
                        break;
                    case 2:
                        showText = (<span style={{ color: 'green' }}>{val?.name}</span>);
                        break;
                    case 4:
                        showText = (<span style={{ color: 'blue' }}>{val?.name}</span>);
                        break;
                    default:
                        <>{val?.name}</>
                        break;
                }
                return showText;
            }
        },
        {
            title: '来源',
            dataIndex: 'scope',
            sorter: true,
            render: (text: any) => {
                return showText(text, { showLength: 15, pos: 'suffix' });
            }
        },
        {
            title: '信息',
            dataIndex: 'info',
            sorter: true,
            render: (text: any) => {
                return showText(text, { showLength: 20 });
            }
        },
        {
            title: '详情',
            dataIndex: 'details',
            render: (text: any) => {
                return showText(text, { showLength: 20 });
            }
        },
        {
            title: '代码',
            dataIndex: 'errorCodeId',
        },
        {
            title: '设备',
            dataIndex: 'device',
            render: (text: any) => <Tooltip placement="topLeft" title={text ? text.deviceName : ''}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.deviceName.length >= 10 ? text.deviceName.substring(0, 10) + '......' : text.deviceName)
                    : ''
            }</Tooltip>
        },
        {
            title: '操作人',
            dataIndex: 'userName',
            render: (text: any) => <Tooltip placement="topLeft" title={text}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.length >= 10 ? text.substring(0, 10) + '......' : text)
                    : ''
            }</Tooltip>
        }
    ];

    const columns = columnsheadId
        .concat(columnsHead as []);

    /**
     * 获取sendPageData
     * @param options 选项
     * @returns 
     */
    const getSendPageData = (options?: {
        selectLoggerTypeId?: string;
        selectDateTime?: Dayjs;
    }) => {
        const { selectLoggerTypeId, selectDateTime } = options || {};
        if (selectLoggerTypeId) {
            setLoggerTypeIdSelect(selectLoggerTypeId);
        }
        let orderDateEx
        if (selectDateTime) {
            orderDateEx = dayjs(selectDateTime).format("YYYY-MM-DD");
            setDateTime(orderDateEx);
        }
        const currentSendPageData = {
            loggerTypeId: selectLoggerTypeId ? selectLoggerTypeId : loggerTypeIdSelect,
            queryType,
            queryTypeExtendId,
            queryId,
            dateTime: orderDateEx ? orderDateEx : undefined
        }
        return currentSendPageData;
    };

    const extendProps: BaseWebUniversalProps = {
        ...props,
        defaultService: loggerServiceUrl!,
        titleName: '日志',
        apiId,
        columns,
        sendPageData: getSendPageData(),
        displayOptions: {
            showStates: false,
            showOperation: !isQuery,
            showAdd: false,
            showEdit: false,
            showQuery: isQuery
        },
        renderHeadExtra: ({ rows, fetchData }) => (
            <>
                <Select
                    showSearch
                    placeholder="日志类型"
                    optionFilterProp="children"
                    defaultValue={'systemLogger'}
                    onChange={(e) => {
                        fetchData({
                            sendPageData: getSendPageData({ selectLoggerTypeId: e })
                        })
                    }}
                    disabled={loggerTypeSelectDisabled}
                    value={loggerTypeIdSelect}
                    style={{ width: 130 }}
                    filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {getList.general(loggerTypeData)}
                </Select>
                <DatePicker
                    allowClear
                    format="YYYY-MM-DD"
                    disabled={loggerDateSelectDisabled}
                    onChange={(e: Dayjs | any) => {
                        fetchData({
                            sendPageData: getSendPageData({ selectDateTime: e })
                        })
                    }} />
                {isLoggerClear === true ? <Button onClick={() => setModalVisibleBaseLoggerClear(true)}>清除日志</Button> : null}
                {modalVisibleBaseLoggerClear ? (<BaseLoggerClear
                    apiId={apiId}
                    onClose={() => setModalVisibleBaseLoggerClear(false)}
                    loggerServiceUrl={loggerServiceUrl} />) : null}
            </>
        )
    };

    return (
        <>
            <BaseWebUniversal ref={ref} {...extendProps} />
        </>
    )
}

export default Logger;


